import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { trackError } from '#api/errors.ts';
import type { useGoogleIdPSync, useGoogleIdPSyncDryRun, useTestGoogleIdP } from '#api/google-idp.queries.ts';
import { describeGoogleIdPError, GoogleIdPSyncRunError } from '#api/google-idp.ts';
import { AlertIcon, CheckCircle, InfoCircle } from '#assets/ReactSvg.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { Timestamp } from '#components/atoms/timestamp/Timestamp.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import type { GoogleIdPSyncResult, GoogleIdPSyncUser } from '#dn-types/google-idp.ts';
import { DirectoryStatePill } from './DirectoryStatePill.tsx';
import styles from './GoogleDirectorySyncDialog.module.css';

type TestMutation = ReturnType<typeof useTestGoogleIdP>;
type DryRunMutation = ReturnType<typeof useGoogleIdPSyncDryRun>;
type SyncMutation = ReturnType<typeof useGoogleIdPSync>;

type Props = {
  onClose: () => void;
  /**
   * Set when the dialog opened from Test connection: it shows the grant being checked, then the dry run that
   * compares the directory with Defined Networking. Without it, the dialog shows a sync.
   *
   * The mutations belong to the opener, which starts them from its click handler. Starting them from an effect in
   * here loses the result under StrictMode: the simulated unmount detaches useMutation's observer from the in-flight
   * mutation, and nothing reattaches it, so the dialog would sit on its progress message forever.
   */
  test?: TestMutation;
  dryRun: DryRunMutation;
  sync: SyncMutation;
};

/**
 * {@link testThenPreview} checks the stored grant and, once Google accepts it, starts the dry run that shows how the
 * directory differs from what Defined Networking knows. Call it from a click handler; see the dialog's props.
 */
export function testThenPreview(test: TestMutation, dryRun: DryRunMutation) {
  dryRun.reset();
  test.mutate(undefined, { onSuccess: () => dryRun.mutate() });
}

/**
 * {@link GoogleDirectorySyncDialog} shows a Google Workspace user sync. Opened from Sync now, it shows the sync as
 * it runs and what it changed. Opened from Test connection, it checks the grant, then lists what a sync would
 * change without changing anything, and offers to sync.
 */
export function GoogleDirectorySyncDialog({ onClose, test, dryRun, sync }: Props) {
  const elapsed = useElapsedSeconds(dryRun.isPending || sync.isPending);
  // Once a sync has started, it is what the dialog shows, however the dialog was opened.
  const syncing = !sync.isIdle;
  const isPending = Boolean(test?.isPending) || dryRun.isPending || sync.isPending;
  const result = syncing ? sync.data : dryRun.data;
  const canSync = !syncing && dryRun.data?.outcome === 'synced' && hasChanges(dryRun.data);
  // A run that stopped at the deprovisioning limit can go ahead once the admin has reviewed whose hosts are blocked:
  // the limit is raised to exactly the users they saw.
  const limitResult = !isPending && result?.outcome === 'deprovision_limit' ? result : undefined;

  async function runSync(maxDeprovisions?: number) {
    try {
      await sync.mutateAsync({ maxDeprovisions });
    } catch (err) {
      if (!(err instanceof GoogleIdPSyncRunError)) {
        trackError(err);
      }
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} className={styles.GoogleDirectorySyncDialog}>
      <ModalHeader>
        {sync.data?.applied ?
          'Google Workspace sync complete'
        : syncing || !test ?
          'Sync with Google Workspace'
        : 'Test Google Workspace connection'}
      </ModalHeader>
      <ModalBody>
        {syncing || !test ?
          <RunView
            label="Syncing with your Google Workspace…"
            mutation={sync}
            isSync={true}
            elapsed={elapsed}
            onRetry={() => void runSync()}
          />
        : <TestView test={test} dryRun={dryRun} elapsed={elapsed} />}
      </ModalBody>
      <ModalFooter>
        <Spacer>
          <Button variant="link" onClick={onClose}>
            {sync.data ? 'Done' : 'Close'}
          </Button>
          {canSync && dryRun.data ?
            <Button onClick={() => void runSync()} disabled={isPending}>
              {syncButtonText(dryRun.data)}
            </Button>
          : null}
          {limitResult ?
            <Button variant="danger" onClick={() => void runSync(limitResult.summary.deprovision)} disabled={isPending}>
              {`Block anyway for ${plural(limitResult.summary.deprovision, 'user')}`}
            </Button>
          : null}
        </Spacer>
      </ModalFooter>
    </Modal>
  );
}

/** The two steps of Test connection: the grant, then how the directory differs from Defined Networking. */
function TestView({ test, dryRun, elapsed }: { test: TestMutation; dryRun: DryRunMutation; elapsed: number }) {
  return (
    <Spacer direction="vertical">
      {test.isPending ?
        <output className="p-md m-0">Checking the connection to Google…</output>
      : test.isError ?
        <Spacer direction="vertical">
          <ErrorMessage>{describeGoogleIdPError(test.error)}</ErrorMessage>
          <div>
            <Button variant="secondary" size="small" onClick={() => testThenPreview(test, dryRun)}>
              Try again
            </Button>
          </div>
        </Spacer>
      : <Banner variant="success" Icon={CheckCircle} iconClassName="color-success">
          The connection works: Google accepted it and the directory can be read.
        </Banner>
      }

      {test.isSuccess ?
        <>
          <h3 className="h5 m-0">Changes a sync would make</h3>
          <RunView
            label="Comparing your Google Workspace with Defined Networking…"
            mutation={dryRun}
            isSync={false}
            elapsed={elapsed}
            onRetry={() => dryRun.mutate()}
          />
        </>
      : null}
    </Spacer>
  );
}

/** A dry run or sync: its progress while it runs, then its result or why it failed. */
function RunView({
  label,
  mutation,
  isSync,
  elapsed,
  onRetry,
}: {
  label: string;
  mutation: DryRunMutation | SyncMutation;
  /** A sync changes hosts and keeps running server-side; a dry run does neither */
  isSync: boolean;
  elapsed: number;
  onRetry: () => void;
}) {
  if (mutation.isPending) {
    return (
      <Spacer direction="vertical" size="narrow">
        <output className="p-md m-0">
          {label}
          {elapsed > 0 ? ` ${elapsed}s` : ''}
        </output>
        <p className="p-sm m-0">
          Every user in the directory is read, so a large Workspace can take a few minutes.
          {isSync ? ' The sync keeps running if you close this window.' : ''}
        </p>
      </Spacer>
    );
  }
  if (mutation.isError) {
    return (
      <Spacer direction="vertical">
        <ErrorMessage>{describeGoogleIdPError(mutation.error)}</ErrorMessage>
        {isSync ?
          <p className="p-md m-0">Some changes may have been made before the sync stopped.</p>
        : null}
        <div>
          <Button variant="secondary" size="small" onClick={onRetry}>
            Try again
          </Button>
        </div>
      </Spacer>
    );
  }
  return mutation.data ? <SyncResult result={mutation.data} /> : null;
}

/** Whole seconds since `running` last became true; 0 while it is false. */
function useElapsedSeconds(running: boolean) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!running) {
      return;
    }
    const started = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => {
      clearInterval(id);
      setElapsed(0);
    };
  }, [running]);

  return elapsed;
}

function syncButtonText(result: GoogleIdPSyncResult) {
  const { blockHosts } = result.summary;
  return blockHosts > 0 ? `Sync now and block ${plural(blockHosts, 'host')}` : 'Sync now';
}

function SyncResult({ result }: { result: GoogleIdPSyncResult }) {
  const { summary, applied, deleteAfterDays } = result;
  const changes = result.users.filter((u) => isChange(u));
  const directoryChanges = summary.create + summary.update + summary.delete;
  const failed = result.users.some((u) => u.error);
  // The summary counts what the run planned. Only when every planned change went through can it say they were made;
  // otherwise the skipped and failed lines, and the table, say which were not.
  const done = applied && summary.skippedHosts === 0 && !failed;

  return (
    <Spacer direction="vertical">
      <OutcomeBanner result={result} />

      <p className="p-md m-0">
        Checked {plural(summary.users, 'user')}
        {result.syncedAt ?
          <>
            {' '}
            at <Timestamp datetime={result.syncedAt} />
          </>
        : null}
        , {summary.enrolled} with enrolled hosts.
      </p>

      {result.outcome === 'synced' || result.outcome === 'deprovision_limit' || result.applied ?
        changes.length === 0 ?
          <Banner variant="success" Icon={CheckCircle} iconClassName="color-success">
            {applied ? 'Host access now matches the directory.' : 'Host access already matches the directory.'}
          </Banner>
        : <>
            <ul className={styles.GoogleDirectorySyncDialog_summary}>
              {summary.blockHosts > 0 ?
                <li>
                  {done ? 'Blocked' : 'Block'} {plural(summary.blockHosts, 'host')} belonging to{' '}
                  {plural(summary.deprovision, 'user')} suspended, archived or deleted in Google
                </li>
              : null}
              {summary.restoreHosts > 0 ?
                <li>
                  {done ? 'Unblocked' : 'Unblock'} {plural(summary.restoreHosts, 'host')} of users who are active again
                </li>
              : null}
              {summary.deleteHosts > 0 ?
                <li>
                  {done ? 'Deleted' : 'Delete'} {plural(summary.deleteHosts, 'host')} blocked for more than{' '}
                  {plural(deleteAfterDays, 'day')}
                </li>
              : null}
              {failed ?
                <li>Some host changes failed; the users below say which, and the next sync tries them again</li>
              : null}
              {summary.skippedHosts > 0 ?
                <li>Left {plural(summary.skippedHosts, 'host')} alone because an admin changed them during the sync</li>
              : null}
              {summary.skipped > 0 ?
                <li>
                  Left {plural(summary.skipped, 'user')} alone because Google’s answer changed during the sync; the next
                  sync checks them again
                </li>
              : null}
              {directoryChanges > 0 ?
                <li>
                  {applied ? 'Recorded' : 'Record'} directory changes for {plural(directoryChanges, 'user')}
                </li>
              : null}
            </ul>
            <ChangesTable users={changes} applied={applied} />
            {result.usersTruncated ?
              <p className="p-sm m-0">
                Showing the first {changes.length} changes. The counts above include every user.
              </p>
            : null}
            {!applied && result.outcome === 'synced' ?
              <p className="p-sm m-0">
                Nothing has changed yet. Syncing reads the directory again, so the result can differ if it changed in
                the meantime. Blocked hosts keep their configuration and are unblocked if their user is restored in
                Google
                {deleteAfterDays > 0 ? `, unless they stay blocked for ${plural(deleteAfterDays, 'day')}` : ''}.
              </p>
            : null}
          </>

      : null}
    </Spacer>
  );
}

function OutcomeBanner({ result }: { result: GoogleIdPSyncResult }) {
  switch (result.outcome) {
    case 'synced':
      return null;
    case 'paused':
      return (
        <Banner variant="info" Icon={InfoCircle}>
          Sync is paused because none of your Google endpoint SSO providers are enabled. Nothing was changed.
        </Banner>
      );
    case 'disabled':
      return (
        <Banner variant="info" Icon={InfoCircle}>
          Google Workspace sync is not enabled for your organization. Nothing was changed.
        </Banner>
      );
    case 'deprovision_limit':
      return (
        <Banner variant="alert" Icon={AlertIcon}>
          {plural(result.summary.deprovision, 'user')} would have their hosts blocked, more than the{' '}
          {result.deprovisionLimit} a sync blocks without review, so nothing was changed. Check that the right Workspace
          is connected and that everyone below should lose access. If they should, block anyway.
        </Banner>
      );
    case 'backing_off':
    case 'failed':
      return (
        <Banner variant="alert" Icon={AlertIcon}>
          The sync did not finish{result.message ? `: ${result.message}` : '.'}
          {result.applied ? ' The host changes shown below were made.' : ' Nothing was changed.'}
        </Banner>
      );
  }
}

const columnHelper = createColumnHelper<GoogleIdPSyncUser>();

function makeColumns(applied: boolean) {
  return [
    columnHelper.accessor('email', {
      header: 'User',
      meta: { truncate: true, fill: true },
      size: 220,
      cell(info) {
        const { name } = info.row.original;
        return (
          <div className="flex flex-col">
            <span className="truncate" title={info.getValue()}>
              {info.getValue()}
            </span>
            {name ?
              <span className="p-sm mb-0 truncate">{name}</span>
            : null}
          </div>
        );
      },
    }),
    columnHelper.accessor('state', {
      header: 'Directory status',
      size: 140,
      cell(info) {
        return <DirectoryStatePill state={info.getValue()} />;
      },
    }),
    columnHelper.display({
      id: 'change',
      header: applied ? 'Change made' : 'Change',
      size: 220,
      cell(info) {
        const u = info.row.original;
        return (
          <div className="flex flex-col">
            {describeChange(u, applied).map((line) => (
              <span key={line}>{line}</span>
            ))}
            {u.error ?
              <ErrorMessage>{u.error}</ErrorMessage>
            : null}
          </div>
        );
      },
    }),
  ];
}

function ChangesTable({ users, applied }: { users: GoogleIdPSyncUser[]; applied: boolean }) {
  return (
    <div className={styles.GoogleDirectorySyncDialog_table}>
      <Table
        data={users}
        columns={makeColumns(applied)}
        fixedLayout={true}
        rowHeight="tall"
        caption={applied ? 'Changes made by the sync' : 'Changes the sync would make'}
      />
    </div>
  );
}

function hostChangeCount(u: GoogleIdPSyncUser) {
  return u.blockHostIDs.length + u.restoreHostIDs.length + u.deleteHostIDs.length;
}

function isChange(u: GoogleIdPSyncUser) {
  return u.rowAction !== 'unchanged' || hostChangeCount(u) > 0 || Boolean(u.skipped) || Boolean(u.error);
}

function hasChanges({ summary }: GoogleIdPSyncResult) {
  return (
    summary.create + summary.update + summary.delete + summary.blockHosts + summary.restoreHosts + summary.deleteHosts >
    0
  );
}

const HOST_STEPS = [
  { key: 'blockHostIDs', planned: 'Block', done: 'Blocked', verb: 'block' },
  { key: 'restoreHostIDs', planned: 'Unblock', done: 'Unblocked', verb: 'unblock' },
  { key: 'deleteHostIDs', planned: 'Delete', done: 'Deleted', verb: 'delete' },
] as const;

/** One line per thing the sync does to a user, in the tense of whether it has happened. */
export function describeChange(u: GoogleIdPSyncUser, applied: boolean): string[] {
  const lines: string[] = [];

  for (const step of HOST_STEPS) {
    const hostIDs = u[step.key];
    if (hostIDs.length === 0) {
      continue;
    }
    if (!applied) {
      lines.push(`${step.planned} ${plural(hostIDs.length, 'host')}`);
      continue;
    }
    const changed = hostIDs.filter((id) => !u.skippedHostIDs.includes(id)).length;
    if (!u.applied) {
      // u.error names the hosts that failed.
      lines.push(`Could not ${step.verb} every host`);
    } else if (changed > 0) {
      lines.push(`${step.done} ${plural(changed, 'host')}`);
    }
  }
  if (applied && u.skippedHostIDs.length > 0) {
    lines.push(`Left ${plural(u.skippedHostIDs.length, 'host')} alone: changed during the sync`);
  }
  if (u.skipped) {
    lines.push(u.skipped);
  }

  switch (u.rowAction) {
    case 'create':
      lines.push('New in the directory');
      break;
    case 'delete':
      lines.push('No longer in the directory');
      break;
    case 'update':
      lines.push(u.previousState && u.previousState !== u.state ? `Was ${u.previousState}` : 'Name or email changed');
      break;
    case 'unchanged':
      break;
  }

  return lines;
}

function plural(n: number, noun: string) {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}
